import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.tsx';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { Badge } from './ui/badge.tsx';
import { Building2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import type {
  AssignedProperty,
  CreateUserResponse,
  Customer,
  CustomerRbacRole,
} from '@metron/shared';
import {
  CUSTOMER_RBAC_ROLE_LABELS,
  defaultRoleForCustomer,
  grantableRoles,
  isPropertyManagerRole,
  rolesForCustomer,
} from '@metron/shared';
import { useCustomers } from '../hooks/useCustomers.ts';
import { saveIdentityThenAccess } from '../lib/user-writes.ts';
import { usePermissions } from '../hooks/usePermissions.ts';
import { CustomerRoleStep } from './user-form/CustomerRoleStep.tsx';
import { createLogger, toErrorMessage } from '@metron/shared';

const logger = createLogger('CreateUserModal');

type UserType = 'customer' | 'metron-internal';

interface CreateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateUser: (userData: { email: string; name: string }) => Promise<CreateUserResponse>;
  /**
   * When set, the customer is fixed to this id: pre-selected, the picker shown
   * but not interactive, and the user type pinned to "Customer user". Used by
   * the customer detail page, where the customer is not in question.
   * MET-3237 row 8.
   */
  lockedCustomerId?: number;
}

export function CreateUserModal(
  { open, onOpenChange, onCreateUser, lockedCustomerId }: CreateUserModalProps,
) {
  const customerLocked = lockedCustomerId != null;

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [createdUser, setCreatedUser] = useState<CreateUserResponse | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    lockedCustomerId ?? null,
  );
  const [userType, setUserType] = useState<UserType>('customer');
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  // Property state — driven by the PM role within CustomerRoleStep
  const [availableProperties, setAvailableProperties] = useState<AssignedProperty[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);

  const {
    customers,
    updateUserCustomers,
    getCustomerProperties,
    updateUserProperties,
    invalidateUsers,
  } = useCustomers();
  const { isAdmin, isSupport } = usePermissions();

  const selectedCustomer: Customer | undefined = customers.find(
    (c) => c.id === selectedCustomerId,
  );

  // [] until a customer is selected — safe because CustomerRoleStep guards on a
  // selected customer before rendering any role control that consumes this.
  const grantable = useMemo(
    () =>
      selectedCustomer
        ? grantableRoles(
          rolesForCustomer(selectedCustomer.accountType, selectedCustomer.realEstateAccountType),
          { isAdmin, isSupport },
        )
        : [],
    [selectedCustomer, isAdmin, isSupport],
  );

  const isPropertyMgr = isPropertyManagerRole(selectedRole);

  const fetchProperties = (customerId: number) => {
    setAvailableProperties([]);
    setLoadingProperties(true);
    getCustomerProperties(customerId)
      .then(setAvailableProperties)
      .catch((error) => {
        logger.error({ error }, 'Failed to load customer properties');
        toast.error('Failed to load properties');
      })
      .finally(() => setLoadingProperties(false));
  };

  // Fetch properties whenever the selected customer + role combination calls for it.
  useEffect(() => {
    if (selectedCustomerId != null && isPropertyMgr) {
      fetchProperties(selectedCustomerId);
    } else {
      setAvailableProperties([]);
      setSelectedPropertyIds([]);
      setLoadingProperties(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, isPropertyMgr]);

  // A locked customer is pre-selected, so the role a picker click would have
  // chosen has to be seeded here instead (the picker is never clicked).
  useEffect(() => {
    if (!open || !customerLocked || selectedRole != null) return;
    const customer = customers.find((c) => c.id === lockedCustomerId);
    if (!customer) return;
    const roles = grantableRoles(
      rolesForCustomer(customer.accountType, customer.realEstateAccountType),
      { isAdmin, isSupport },
    );
    setSelectedRole(
      roles[0] ?? defaultRoleForCustomer(customer.accountType, customer.realEstateAccountType),
    );
  }, [open, customerLocked, lockedCustomerId, selectedRole, customers, isAdmin, isSupport]);

  // Reset role when customer changes
  const handleCustomerChange = (customerId: number) => {
    // Never reachable while locked: CustomerPicker is disabled there.
    if (customerLocked) return;
    setSelectedCustomerId(customerId);
    const customer = customers.find((c) => c.id === customerId);
    if (customer) {
      const roles = grantableRoles(
        rolesForCustomer(customer.accountType, customer.realEstateAccountType),
        { isAdmin, isSupport },
      );
      // Auto-select the first grantable role, falling back to the default role
      setSelectedRole(
        roles[0] ?? defaultRoleForCustomer(customer.accountType, customer.realEstateAccountType),
      );
    }
    setSelectedPropertyIds([]);
    setAvailableProperties([]);
  };

  const handleCustomerClear = () => {
    setSelectedCustomerId(null);
    setSelectedRole(null);
  };

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
    // Clear property selection when switching away from PM role
    if (!isPropertyManagerRole(role)) {
      setSelectedPropertyIds([]);
      setAvailableProperties([]);
    }
  };

  const handlePropertyToggle = (propertyId: number, checked: boolean) => {
    setSelectedPropertyIds((prev) =>
      checked ? [...prev, propertyId] : prev.filter((id) => id !== propertyId)
    );
  };

  const handleUserTypeChange = (type: UserType) => {
    // The type control is disabled while locked: a customer-page create always
    // produces a customer user for that customer.
    if (customerLocked) return;
    setUserType(type);
    // Reset access state when switching types
    setSelectedCustomerId(null);
    setSelectedRole(null);
    setAvailableProperties([]);
    setSelectedPropertyIds([]);
  };

  // A Metron-internal user has no customer access to fill in.
  const hasAccessSection = userType !== 'metron-internal';

  // Access needs a customer and — for a PM role — at least one property.
  const accessIncomplete = hasAccessSection &&
    (selectedCustomerId == null || (isPropertyMgr && selectedPropertyIds.length === 0));

  // One form, so one validity check for the whole thing.
  const createDisabled = isLoading || !name || !email || accessIncomplete;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !name) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      // Identity first, then access — the users query is invalidated once, after
      // the access write. See lib/user-writes.ts for why the order matters.
      await saveIdentityThenAccess({
        writeIdentity: async () => {
          const result = await onCreateUser({ email, name });
          setCreatedUser(result);
          return result;
        },
        writeAccess: () =>
          isPropertyMgr
            // Property-scoped access: assign properties only.
            // The backend revokes any existing customer grant and sets the PM group.
            ? updateUserProperties(email, { propertyIds: selectedPropertyIds })
            // Update customer association after user creation (include role if set for non-internal)
            : updateUserCustomers(email, {
              customerId: userType === 'metron-internal' ? null : selectedCustomerId,
              isMetronInternal: userType === 'metron-internal',
              ...(selectedRole != null && userType === 'customer' ? { role: selectedRole } : {}),
            }),
        invalidateUsers,
      });

      toast.success('User created successfully');
    } catch (error) {
      const errorMessage = toErrorMessage(error, 'Failed to create user');
      toast.error(errorMessage);
      logger.error({ error }, 'Failed to create user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setName('');
    setCreatedUser(null);
    setSelectedCustomerId(lockedCustomerId ?? null);
    setUserType('customer');
    setSelectedRole(null);
    setAvailableProperties([]);
    setSelectedPropertyIds([]);
    onOpenChange(false);
  };

  // Escape, the X button, and outside clicks must reset the wizard too —
  // otherwise reopening restores the stale selection.
  const handleDialogOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      onOpenChange(true);
    } else {
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className='sm:max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden'>
        {!createdUser
          ? (
            <>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Create a new Waterscope Plus user account and assign their customer and role. The
                  user will receive a welcome email with a temporary password.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className='flex min-h-0 flex-1 flex-col'>
                <div className='grid min-h-0 flex-1 gap-4 overflow-y-auto py-4 pr-1'>
                  <div className='grid gap-2'>
                    <Label htmlFor='name'>Full Name</Label>
                    <Input
                      id='name'
                      type='text'
                      placeholder='John Doe'
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                  <div className='grid gap-2'>
                    <Label htmlFor='email'>Email</Label>
                    <Input
                      id='email'
                      type='email'
                      placeholder='user@example.com'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>

                  {/* User type toggle */}
                  <div className='grid gap-2'>
                    <span className='text-sm font-medium'>User Type</span>
                    <div
                      role='group'
                      aria-label='User type'
                      className='flex rounded-md border overflow-hidden w-fit'
                    >
                      <button
                        type='button'
                        onClick={() => handleUserTypeChange('customer')}
                        disabled={isLoading || customerLocked}
                        className={`px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          userType === 'customer'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background text-foreground enabled:hover:bg-accent'
                        }`}
                      >
                        Customer user
                      </button>
                      <button
                        type='button'
                        onClick={() => handleUserTypeChange('metron-internal')}
                        disabled={isLoading || customerLocked}
                        className={`px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          userType === 'metron-internal'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background text-foreground enabled:hover:bg-accent'
                        }`}
                      >
                        Metron internal
                      </button>
                    </div>
                    {userType === 'metron-internal' && (
                      <p className='text-sm text-muted-foreground'>
                        This user will have access to internal Metron tools and features.
                      </p>
                    )}
                  </div>

                  {/* Access — hidden for Metron-internal users, who hold no grant */}
                  {hasAccessSection && (
                    <div className='border-t pt-4'>
                      <CustomerRoleStep
                        customers={customers}
                        selectedCustomerId={selectedCustomerId}
                        onCustomerChange={handleCustomerChange}
                        onCustomerClear={customerLocked ? undefined : handleCustomerClear}
                        customerLocked={customerLocked}
                        selectedRole={selectedRole}
                        onRoleChange={handleRoleChange}
                        grantable={grantable}
                        disabled={isLoading}
                        availableProperties={availableProperties}
                        loadingProperties={loadingProperties}
                        selectedPropertyIds={selectedPropertyIds}
                        onPropertyToggle={handlePropertyToggle}
                      />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button type='submit' disabled={createDisabled}>
                    {isLoading ? 'Creating...' : 'Create user'}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )
          : (
            <>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <CheckCircle2 className='h-5 w-5 text-primary' />
                  User Created Successfully
                </DialogTitle>
                <DialogDescription>
                  A welcome email with login credentials has been sent to the user.
                </DialogDescription>
              </DialogHeader>
              <div className='grid gap-4 py-4'>
                <div className='grid gap-2'>
                  <Label>Email</Label>
                  <div className='text-sm font-mono bg-muted p-2 rounded'>
                    {createdUser.user.email}
                  </div>
                </div>
                <div className='grid gap-2'>
                  <Label>Name</Label>
                  <div className='text-sm bg-muted p-2 rounded'>
                    {createdUser.user.name}
                  </div>
                </div>
                {userType === 'metron-internal' && (
                  <div className='flex items-center gap-2 text-sm'>
                    <Badge variant='secondary'>Metron Internal</Badge>
                  </div>
                )}
                {isPropertyMgr && (
                  <div className='flex items-center gap-2 text-sm'>
                    <Badge variant='secondary'>Property Manager</Badge>
                    <span className='text-muted-foreground text-xs'>
                      {selectedPropertyIds.length}{' '}
                      propert{selectedPropertyIds.length === 1 ? 'y' : 'ies'} assigned
                    </span>
                  </div>
                )}
                {userType === 'customer' && !isPropertyMgr && selectedCustomer && (
                  <div className='grid gap-2'>
                    <Label>Customer Assignment</Label>
                    <Badge variant='outline' className='text-xs w-fit'>
                      <Building2 className='h-3 w-3 mr-1' />
                      {selectedCustomer.waterscopeAccountName}
                    </Badge>
                  </div>
                )}
                {userType === 'customer' && !isPropertyMgr && selectedRole && (
                  <div className='grid gap-2'>
                    <Label>Role</Label>
                    <Badge variant='outline' className='text-xs w-fit'>
                      {CUSTOMER_RBAC_ROLE_LABELS[selectedRole as CustomerRbacRole]}
                    </Badge>
                  </div>
                )}
                <p className='text-sm text-muted-foreground'>
                  The user will receive an email from AWS Cognito with their temporary password.
                  They will be required to change it on first login.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={handleClose}>Done</Button>
              </DialogFooter>
            </>
          )}
      </DialogContent>
    </Dialog>
  );
}
